import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMsal } from '@azure/msal-react'
import { InteractionStatus } from '@azure/msal-browser'
import { loginRequest } from '../../authConfig/AuthConfig'
import { useLazyGetAuthorizeQuery } from '../../api/auth/AuthApi'
import { UI_CONSTANTS } from '../../utils/constants/apiConstants'
import { setAtlasData } from '../../redux/reducers/AtlasSandReducer'
import { RootState } from '../../store/Store'
import useApplicationConfigQuery from '../applicationConfig/useApplicationConfigQuery'

export const useSSOAuth = () => {
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const [authChecked, setAuthChecked] = useState(false)
  const { getApplicationConfig } = useApplicationConfigQuery()
  const isEmergencyEnabled = useSelector(
    (state: RootState) => state?.atlas?.isFireFighterAccessEnabled,
  )

  const { instance, accounts, inProgress } = useMsal()
  const [getAuthorize] = useLazyGetAuthorizeQuery()

  useEffect(() => {
    // Wait until mode is confirmed — null means getAuthConfig hasn't resolved yet
    if (isEmergencyEnabled === null) return
    // Pure firefighter user (no MSAL accounts) — MSAL path blocked
    if (isEmergencyEnabled === true && accounts.length === 0) return
    if (accounts.length > 0) {
      setIsLoading(true)
      setAuthChecked(false)
      instance
        .acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        })
        .then((response) => {
          instance.setActiveAccount(accounts[0])
          setAuthChecked(true)
          dispatch(
            setAtlasData({
              method: UI_CONSTANTS.USER,
              data: { user: accounts[0], idToken: response.accessToken },
            }),
          )
          getAuthorize(response.accessToken)
            .unwrap()
            .then((data) => {
              setIsLoading(false)
              dispatch(
                setAtlasData({
                  method: UI_CONSTANTS.AUTHORIZED_USER,
                  data,
                }),
              )
              getApplicationConfig([])
            })
            .catch((error) => {
              setIsLoading(false)
              console.error('getAuthorize failed', error)
              // Do NOT call logoutRedirect on 401 — it clears the MSAL session,
              // which causes Login.tsx to fire loginRedirect again → infinite loop.
            })
        })
        .catch((error) => {
          console.log('Redirect login', error)
          setIsLoading(false)
          const interactiveErrors = [
            'no_tokens_found',
            'monitor_window_timeout',
            'interaction_required',
            'login_required',
            'consent_required',
          ]
          if (interactiveErrors.includes(error.errorCode)) {
            if (inProgress === InteractionStatus.None) {
              instance.loginRedirect(loginRequest).catch((e: Error) => {
                console.log(e)
              })
            }
          }
        })
    }
  }, [accounts, isEmergencyEnabled])

  return { isLoading, authChecked }
}
